import { useRef, useState } from "react";
import { FileText, Film, ImagePlus, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, mediaUrl } from "@/lib/api";

const MAX_BYTES = 50 * 1024 * 1024;
const VIDEO_MAX_SECONDES = 60;

const dureeVideo = (file) =>
  new Promise((resolve) => {
    const el = document.createElement("video");
    el.preload = "metadata";
    el.onloadedmetadata = () => {
      URL.revokeObjectURL(el.src);
      resolve(el.duration || 0);
    };
    el.onerror = () => resolve(0);
    el.src = URL.createObjectURL(file);
  });

const Icone = ({ type }) => {
  if (type?.startsWith("video/")) return <Film size={14} />;
  if (type === "application/pdf") return <FileText size={14} />;
  return <ImagePlus size={14} />;
};

/** Upload de médias (photo, PDF, vidéo courte) vers le stockage objet. */
export const MediaUpload = ({ medias = [], onChange, endpoint = "/tickets/media", accept = "image/*,video/*,application/pdf", label = "Photo, PDF ou vidéo (60 s max)", testid = "media-upload", max = 6 }) => {
  const [busy, setBusy] = useState(false);
  const input = useRef(null);

  const envoyer = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    if (medias.length + files.length > max) return toast.error(`${max} fichiers maximum`);
    setBusy(true);
    try {
      const ajoutes = [];
      for (const file of files) {
        if (file.size > MAX_BYTES) {
          toast.error(`${file.name} dépasse 50 Mo`);
          continue;
        }
        if (file.type.startsWith("video/")) {
          const secondes = await dureeVideo(file);
          if (secondes > VIDEO_MAX_SECONDES + 1) {
            toast.error(`${file.name} dépasse 60 secondes`);
            continue;
          }
        }
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post(endpoint, fd, { headers: { "Content-Type": "multipart/form-data" } });
        ajoutes.push({ url: data.url, type: data.content_type, nom: file.name, taille: data.taille });
      }
      if (ajoutes.length) {
        onChange([...medias, ...ajoutes]);
        toast.success(`${ajoutes.length} fichier(s) ajouté(s)`);
      }
    } catch (err) {
      toast.error(errMsg(err, "Envoi impossible"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2" data-testid={testid}>
      <div className="bk-label">{label}</div>
      {medias.length > 0 && (
        <div className="grid grid-cols-3 gap-2">
          {medias.map((m) => (
            <div key={m.url} data-testid={`${testid}-item-${m.url.split("/").pop()}`} className="relative rounded-xl border border-slate-200 overflow-hidden bg-slate-50">
              {m.type?.startsWith("image/") ? (
                <img src={mediaUrl(m.url)} alt={m.nom} className="h-24 w-full object-cover" />
              ) : m.type?.startsWith("video/") ? (
                <video src={mediaUrl(m.url)} className="h-24 w-full object-cover" muted playsInline />
              ) : (
                <div className="h-24 grid place-items-center text-slate-500">
                  <FileText size={22} />
                </div>
              )}
              <div className="px-2 py-1.5 flex items-center gap-1.5 text-[11px] text-slate-600 truncate">
                <Icone type={m.type} />
                <span className="truncate">{m.nom || "fichier"}</span>
              </div>
              <button
                data-testid={`${testid}-remove-${m.url.split("/").pop()}`}
                onClick={() => onChange(medias.filter((x) => x.url !== m.url))}
                className="absolute top-1.5 right-1.5 bg-white/95 rounded-full border border-slate-200 p-1 text-slate-400 hover:text-red-500 shadow-sm"
              >
                <Trash2 size={11} />
              </button>
            </div>
          ))}
        </div>
      )}
      <input ref={input} data-testid={`${testid}-input`} type="file" accept={accept} multiple onChange={envoyer} className="block w-full text-sm text-slate-600" />
      {busy && (
        <p className="text-xs text-slate-500 flex items-center gap-1.5">
          <Loader2 size={12} className="animate-spin" /> Envoi en cours…
        </p>
      )}
    </div>
  );
};

export default MediaUpload;
